import { ChangeDetectionStrategy, Component, DestroyRef, HostListener, computed, effect, inject, input, signal, untracked, viewChild } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { HlmButtonDirective } from '@spartan-ng/helm/button';
import { HlmCheckboxComponent } from '@spartan-ng/helm/checkbox';
import { HlmSwitchComponent } from '@spartan-ng/helm/switch';
import { HlmTooltipTriggerDirective } from '@spartan-ng/helm/tooltip';
import { BackgroundExecutionService } from '@core/services/background-execution.service';
import { TemplateStore } from '@features/catalog/store/template.store';
import { CurrentOrganizationStore } from '@features/organizations/store/current-organization.store';
import { InlineNotice } from '@shared/components/inline-notice/inline-notice';
import { SidePanel } from '@shared/components/side-panel/side-panel';
import { StepTimeline } from '@shared/components/step-timeline/step-timeline';
import { TimelineStep } from '@shared/models/step-timeline.model';
import { CampaignsService } from '../../services/campaigns.service';
import { CampaignWorkflowStore, RunPanelOptions } from '../../store/campaign-workflow.store';
import { PIPELINE_STEPS, RunAction, SKIP_LABELS, STAGE_META, WorkItem, WorkflowRunRequest, WorkflowStage, runTimeline } from '../../models/campaign-workflow.model';

const PREVIEW_DEBOUNCE_MS = 300;
const LAST_TASK = 5;
const FIRST_PLANNING_TASK = 4;
const LAST_DETECTION_TASK = 3;
const KEEP_TEMPLATE = 'keep';
const DEFAULT_LIMIT = 100;
/** The stages of the Analysen tab that can run. Fertige are replanned one by one, from the table. */
const COLUMNS: { name: string; stages: WorkflowStage[] }[][] = [
  [{ name: 'Berechnen', stages: ['new', 'error'] }, { name: 'Neu planen', stages: ['replan'] }],
  [{ name: 'Prüfen', stages: ['review', 'seen', 'flagged'] }],
];
const RUNNABLE = COLUMNS.flat().flatMap((group) => group.stages);
const STILL_DETECTING: WorkflowStage[] = ['new', 'error'];

/** Nothing starts directly: which analyses, how many, up to which step, 2D or 3D, which template. */
@Component({
  selector: 'app-campaign-run-panel',
  standalone: true,
  imports: [DecimalPipe, HlmButtonDirective, HlmCheckboxComponent, HlmSwitchComponent, HlmTooltipTriggerDirective, InlineNotice, SidePanel, StepTimeline],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './campaign-run-panel.html',
})
export class CampaignRunPanel {
  readonly options = input.required<RunPanelOptions>();
  readonly panel = viewChild(SidePanel);

  readonly store = inject(CampaignWorkflowStore);
  readonly templates = inject(TemplateStore);
  private readonly api = inject(CampaignsService);
  private readonly background = inject(BackgroundExecutionService);
  private readonly org = inject(CurrentOrganizationStore);

  readonly columns = COLUMNS;
  readonly meta = STAGE_META;
  readonly timelineConfig = { variant: 'rail', showLines: true, showDescriptions: true, orientation: 'horizontal', animated: true, interactive: true, fitRail: true } as const;
  readonly chosen = signal<Set<WorkflowStage> | null>(null);
  readonly how = signal<'all' | 'n'>('all');
  readonly limit = signal(DEFAULT_LIMIT);
  readonly toTask = signal<number | null>(null);
  readonly hoveredLast = signal<number | null>(null);
  readonly technical = signal(false);
  readonly templateId = signal<string | null>(null);
  readonly preview = signal<{ runnable: number; skipped: Record<string, number> } | null>(null);
  readonly starting = signal(false);
  readonly runError = signal<string | null>(null);
  private previewTimer?: ReturnType<typeof setTimeout>;
  private previewRequest = 0;

  /** A selection or one analysis: nothing left to choose about which ones. */
  readonly fixed = computed(() => !!this.options().ids);
  readonly heading = computed(() => (this.options().single ? 'Neu planen' : 'Berechnung einrichten'));
  readonly subtitle = computed(() => {
    const { single, ids } = this.options();
    if (single) return single;
    if (ids) return `${ids.length.toLocaleString('de-DE')} ausgewählte Analysen`;
    return 'Die Berechnung läuft im Hintergrund weiter';
  });

  readonly targets = computed<Set<WorkflowStage>>(() => {
    const chosen = this.chosen();
    if (chosen) return chosen;
    const counts = this.store.counts();
    const wanted = this.options().target;
    if (wanted && counts?.[wanted]) return new Set([wanted]);
    const first = RUNNABLE.find((stage) => counts?.[stage]);
    return new Set(first ? [first] : []);
  });
  readonly targetCount = computed(() => {
    const counts = this.store.counts();
    return [...this.targets()].reduce((sum, stage) => sum + (counts?.[stage] ?? 0), 0);
  });
  /** The analyses this run is about, as far as the list knows them. */
  readonly rows = computed<WorkItem[]>(() => {
    const { ids } = this.options();
    if (ids) {
      const items = this.store.itemById();
      return ids.flatMap((id) => { const item = items.get(id); return item ? [item] : []; });
    }
    const targets = this.targets();
    const rows = (this.store.items() ?? []).filter((item) => targets.has(item.stage));
    return this.how() === 'n' ? rows.slice(0, Math.max(1, Math.round(this.limit()) || 1)) : rows;
  });
  private readonly detecting = computed(() => {
    const { ids, single } = this.options();
    if (single) return false;
    return ids ? this.rows().some((row) => STILL_DETECTING.includes(row.stage)) : [...this.targets()].some((stage) => STILL_DETECTING.includes(stage));
  });
  /** Replanning keeps detection and corrections; it can only start at planning. */
  readonly replanning = computed(() => !this.detecting());
  readonly from = computed(() => (this.replanning() ? FIRST_PLANNING_TASK : 1));
  readonly last = computed(() => Math.max(this.toTask() ?? this.options().to ?? LAST_TASK, this.from()));
  readonly action = computed<RunAction>(() => (this.replanning() ? 'plan' : this.last() <= LAST_DETECTION_TASK ? 'detect' : 'full'));
  readonly needsTemplate = computed(() => this.last() >= FIRST_PLANNING_TASK);
  readonly templateOptions = computed(() => [
    ...(this.replanning() ? [{ id: KEEP_TEMPLATE, name: 'Aktuelle Vorlage je Analyse behalten' }] : []),
    ...this.templates.allTemplates().map((template) => ({ id: template.id, name: template.name })),
  ]);
  readonly template = computed(() => {
    const chosen = this.templateId();
    const options = this.templateOptions();
    return chosen && options.some((option) => option.id === chosen) ? chosen : options[0]?.id ?? null;
  });

  readonly steps = computed(() =>
    runTimeline(this.rows(), this.technical(), {
      hoveredLast: this.hoveredLast(),
      lockedLast: this.toTask() === null ? null : this.last(),
      rangeLast: this.last(),
      from: this.replanning() ? FIRST_PLANNING_TASK : undefined,
      clickable: !this.starting(),
      actionTitle: 'Berechnung starten',
      compact: true,
    }),
  );
  readonly lastStepName = computed(() => PIPELINE_STEPS.find((step) => step.last >= this.last())?.label ?? 'Planung');
  readonly stepHint = computed(() => {
    if (this.from() >= FIRST_PLANNING_TASK) return 'Geodaten und Analyse liegen schon vor. Es wird nur die Planung gerechnet, Korrekturen bleiben erhalten.';
    if (this.last() <= LAST_DETECTION_TASK) return `Stoppt nach ${this.lastStepName()}. Die Planung können Sie später starten.`;
    return 'Alle Schritte in einem Durchgang, in der besten Qualität: 3D, wenn Höhendaten vorliegen, sonst 2D.';
  });

  readonly body = computed<WorkflowRunRequest>(() => {
    const { ids } = this.options();
    const template = this.template();
    return {
      action: this.action(),
      target: ids ? { ids } : { stages: [...this.targets()] },
      toTask: this.last(),
      ...(!ids && this.how() === 'n' ? { limit: Math.max(1, Math.round(this.limit()) || 1) } : {}),
      ...(this.needsTemplate() && template && template !== KEEP_TEMPLATE ? { templateId: template } : {}),
    };
  });
  readonly skippedText = computed(() =>
    Object.entries(this.preview()?.skipped ?? {})
      .map(([reason, count]) => `${count.toLocaleString('de-DE')} ${SKIP_LABELS[reason] ?? reason}`)
      .join(', '),
  );
  readonly blocked = computed(
    () => this.starting() || this.store.running() || !this.preview()?.runnable || (this.needsTemplate() && !this.template()),
  );
  readonly cta = computed(() => {
    if (this.starting()) return 'Wird gestartet…';
    return this.options().single ? 'Neu planen' : `${(this.preview()?.runnable ?? 0).toLocaleString('de-DE')} berechnen`;
  });

  constructor() {
    const org = this.org.organizationId();
    if (org) this.templates.loadTemplatesForOrg(org);
    void this.store.loadItems();
    effect(() => {
      const body = this.body();
      untracked(() => this.schedulePreview(body));
    });
    inject(DestroyRef).onDestroy(() => {
      clearTimeout(this.previewTimer);
      this.previewRequest++;
    });
  }

  toggle(stage: WorkflowStage): void {
    const next = new Set(this.targets());
    if (next.has(stage)) next.delete(stage);
    else next.add(stage);
    this.chosen.set(next);
    this.toTask.set(null);
  }

  onHover(step: TimelineStep | null): void {
    this.hoveredLast.set(step && step.id !== 'created' ? Number(step.id) : null);
  }

  /** First click chooses the step, a second one on its play button starts. */
  pickStep(step: TimelineStep): void {
    if (step.id === 'created') return;
    if (this.toTask() === Number(step.id)) void this.start();
    else this.toTask.set(Number(step.id));
  }

  setLimit(value: string): void {
    this.how.set('n');
    this.limit.set(Number(value));
  }

  private schedulePreview(body: WorkflowRunRequest): void {
    clearTimeout(this.previewTimer);
    const request = ++this.previewRequest;
    const org = this.store.organizationId();
    const campaign = this.store.campaignId();
    if (!org || !campaign) return;
    this.previewTimer = setTimeout(async () => {
      try {
        const preview = await this.api.previewRun(org, campaign, body);
        if (request === this.previewRequest) {
          this.preview.set(preview);
          this.runError.set(null);
        }
      } catch (error) {
        if (request === this.previewRequest) this.runError.set(error instanceof Error ? error.message : 'Vorschau nicht verfügbar.');
      }
    }, PREVIEW_DEBOUNCE_MS);
  }

  async start(): Promise<void> {
    const org = this.store.organizationId();
    const campaign = this.store.campaignId();
    if (!org || !campaign || this.blocked()) return;
    this.starting.set(true);
    this.runError.set(null);
    try {
      const started = await this.api.startRun(org, campaign, this.body());
      this.background.registerBulkRun({ organizationId: org, campaignId: campaign, orgSlug: this.org.organization()?.slug ?? null, snapshot: started.data });
      this.options().onStart?.();
      this.panel()?.close();
      void this.store.refresh();
      void this.store.loadRuns();
    } catch (error) {
      this.runError.set(error instanceof Error ? error.message : 'Die Berechnung konnte nicht gestartet werden. Bitte erneut versuchen.');
    } finally {
      this.starting.set(false);
    }
  }

  @HostListener('window:keydown.enter', ['$event']) onEnter(event: Event): void {
    if (!(event.target as HTMLElement | null)?.closest('input, select, textarea, button, a')) void this.start();
  }
}
